/* eslint-disable no-console */
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import * as Yup from 'yup';

import { HiPlus } from 'react-icons/hi';
import { format, parseISO, set } from 'date-fns';
import Swal from 'sweetalert2';
import { Form } from '@unform/web';
import { FormHandles } from '@unform/core';
import { TableList, Container, Filter, Modal } from './styles';

import Table, { IColumn } from '~/components/Table';
import FiltersLists from '~/components/FiltersLists';
import { KrClose, KrEdit } from '~/components/KoroIcons';
import Toast from '~/utils/toast';
import Input from '~/components/Input';
import Select, { IOption } from '~/components/Select';
import InputDate from '~/components/InputDate';
import InputMask from '~/components/InputMask';
import getValidationErros from '~/utils/getValidationsErrors';
import api from '~/services/api';
import { formatPrice } from '~/utils/format';
import Loading from '~/components/Loading';

interface IDataItem {
  id: number;
  organizer: string;
  fantasy_name: string;
  start_date: string;
  end_date: string;
  status: string;
  service_type: string;
  service_tax: string;
  process_type: string;
  process_tax: string;
  monthly_tax: string;
  free_id?: number;
  free?: string;
  pix_id?: number;
  pix: string;
  card_id?: number;
  card: string;
  billet_id?: number;
  billet: string;
  debit_id?: number;
  debit: string;
  agency: string;
  account: string;
  bank: string;
  pix_key: string;
}

interface IContractValue {
  id: number;
  type: string;
  organizer_deadline: number;
}

interface IContract {
  id: number;
  organizer_id: number;
  code: string;
  organizer: {
    name: string;
  };
  fantasy_name: string;
  start_date: string;
  end_date: string;
  tax_type: string;
  tax: string;
  monthly_tax: string;
  service_type: string;
  service: string;
  koro_deadline: number;
  organizer_deadline: number;
  bank: string;
  agency: string;
  account: string;
  pix_key: string;
  status: string;
  created_at: string;
  contractsValues: IContractValue[];
}

interface IContractResponse {
  data: IContract[];
  from: number;
  to: number;
  total: number;
  pages: number;
}

interface IOrganizer {
  id: number;
  name: string;
}

const Contracts: React.FC = () => {
  const formRef = useRef<FormHandles>(null);
  const [pageSelected, setPageSelected] = useState(1);
  const [data, setData] = useState<IDataItem[]>([]);
  const [tableData, setTableData] = useState({
    from: 0,
    to: 0,
    total: 0,
    totalPage: 0,
  });
  const [organizerSelected, setOrganizerSelected] = useState('');
  const [fantasyNameSelected, setFantasyNameSelected] = useState('');
  const [dateStartSelected, setDateStartSelected] = useState<
    string | undefined
  >(undefined);
  const [dateEndSelected, setDateEndSelected] = useState<string | undefined>(
    undefined
  );
  const [contractSelected, setContractSelected] = useState({} as IDataItem);
  const [show, setShow] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saveContract, setSaveContract] = useState(false);
  const [dateContractStart, setDateContractStart] = useState<Date>(new Date());
  const [dateContractEnd, setDateContractEnd] = useState<Date>(new Date());
  const [contractStatus, setContractStatus] = useState<string>('');
  const [serviceTaxType, setServiceTaxType] = useState('Porcentagem');
  const [processTaxType, setProcessTaxType] = useState('Porcentagem');
  const [organizers, setOrganizers] = useState<IOption[]>([]);
  const [isFree, setIsFree] = useState(false);
  const [isCredit, setIsCredit] = useState(false);

  useEffect(() => {
    api.get<IOrganizer[]>('organizers').then((response) => {
      const organizersData = response.data.map<IOption>((organizer) => ({
        id: organizer.id,
        value: organizer.name,
        selected: false,
      }));

      setOrganizers(organizersData);
    });
  }, []);

  const handleLoadContracts = useCallback(
    async (
      page: number,
      organizer?: string,
      fantasyName?: string,
      dateStart?: string,
      dateEnd?: string
    ) => {
      setLoading(true);
      const response = await api
        .get<IContractResponse>('contracts', {
          params: {
            page,
            organizer,
            fantasyName,
            startDate: dateStart ? new Date(dateStart) : undefined,
            endDate: dateEnd ? new Date(dateEnd) : undefined,
          },
        })
        .finally(() => {
          setLoading(false);
        });

      const contracts = response.data.data.map<IDataItem>((contract) => {
        const freeData = contract.contractsValues.find(
          (value) => value.type === 'Gratuito'
        );
        const pixData = contract.contractsValues.find(
          (value) => value.type === 'Pix'
        );
        const cardData = contract.contractsValues.find(
          (value) => value.type === 'Crédito'
        );
        const billetData = contract.contractsValues.find(
          (value) => value.type === 'Boleto'
        );
        const debitData = contract.contractsValues.find(
          (value) => value.type === 'Débito'
        );
        let free_id;
        let free = '';
        let pix_id;
        let pix = '';
        let card_id;
        let card = '';
        let billet_id;
        let billet = '';
        let debit_id;
        let debit = '';

        if (freeData) {
          free_id = freeData.id;
          free = freeData.organizer_deadline.toString();
        }
        if (pixData) {
          pix_id = pixData.id;
          pix = pixData.organizer_deadline.toString();
        }
        if (cardData) {
          card_id = cardData.id;
          card = cardData.organizer_deadline.toString();
        }
        if (billetData) {
          billet_id = billetData.id;
          billet = billetData.organizer_deadline.toString();
        }
        if (debitData) {
          debit_id = debitData.id;
          debit = debitData.organizer_deadline.toString();
        }
        return {
          id: contract.id,
          organizer: contract.organizer_id.toString(),
          organizer_name: contract.organizer?.name,
          contract_date: format(parseISO(contract.created_at), 'dd/MM/yyyy'),
          fantasy_name: contract.fantasy_name,
          start_date: format(parseISO(contract.start_date), 'dd/MM/yyyy'),
          end_date: format(parseISO(contract.end_date), 'dd/MM/yyyy'),
          status: contract.status,
          service_type: contract.service_type,
          service_tax:
            contract.tax_type === 'Porcentagem'
              ? `${contract.service}%`
              : formatPrice(parseFloat(contract.service)),
          process_type: contract.tax_type,
          process_tax:
            contract.service_type === 'Porcentagem'
              ? `${contract.tax}%`
              : formatPrice(parseFloat(contract.tax)),
          monthly_tax: (parseFloat(contract.monthly_tax) * 100).toFixed(2),
          free_id,
          free,
          pix_id,
          pix,
          card_id,
          card,
          billet_id,
          billet,
          debit_id,
          debit,
          agency: contract.agency,
          account: contract.account,
          bank: contract.bank,
          pix_key: contract.pix_key,
        };
      });
      setData(contracts);
      setTableData({
        from: response.data.from,
        to: response.data.to,
        total: response.data.total,
        totalPage: response.data.pages,
      });
    },
    []
  );

  useEffect(() => {
    handleLoadContracts(1);
  }, [handleLoadContracts]);

  const handleClickDelete = useCallback((organizer) => {
    Swal.fire({
      icon: 'warning',
      iconColor: '#ff6900',
      title: `Excluir contrato de ${organizer.fantasy_name}?`,
      showCancelButton: true,
      cancelButtonText: 'Não',
      confirmButtonText: 'Sim',
      confirmButtonColor: '#ff6900',
      cancelButtonColor: '#B34A00',
      reverseButtons: true,
    }).then(async (e) => {
      if (e.isConfirmed) {
        await api.delete(`contracts/${organizer.id}`);
        setData((oldState) =>
          oldState.filter((organizerData) => organizerData.id !== organizer.id)
        );
        Toast.fire({
          icon: 'success',
          iconColor: '#ff6900',
          title: 'Contrato excluído com sucesso!',
        });
      }
    });
  }, []);

  const handleClickEdit = useCallback((contract) => {
    setIsEdit(true);
    setShow(true);
    setContractSelected(contract);
    setOrganizers((oldState) =>
      oldState.map((organizer) => {
        return {
          ...organizer,
          selected: organizer.id.toString() === contract.organizer,
        };
      })
    );
    const [startDay, startMonth, startYear] = contract.start_date.split('/');
    const startDate = new Date(
      parseInt(startYear, 10),
      parseInt(startMonth, 10) - 1,
      parseInt(startDay, 10)
    );
    const [endDay, endMonth, endYear] = contract.end_date.split('/');
    const endDate = new Date(
      parseInt(endYear, 10),
      parseInt(endMonth, 10) - 1,
      parseInt(endDay, 10)
    );
    setDateContractStart(startDate);
    setDateContractEnd(endDate);
    setContractStatus(contract.status);
    setServiceTaxType(contract.service_type);
    setProcessTaxType(contract.process_type);
    setIsFree(contract.free);
  }, []);

  const columns = useMemo<IColumn[]>(
    () => [
      {
        name: '#',
        selector: 'id',
        sortable: false,
      },
      {
        name: 'Data do Contrato',
        selector: 'contract_date',

        sortable: true,
      },
      {
        name: 'Organizador',
        selector: 'organizer_name',
        sortable: true,
      },
      {
        name: 'Nome Fantasia',
        selector: 'fantasy_name',
        sortable: true,
      },
      {
        name: 'Data Inicio',
        selector: 'start_date',
        sortable: true,
      },
      {
        name: 'Data Fim',
        selector: 'end_date',
        sortable: true,
      },
      {
        name: 'Status',
        selector: 'status',
        sortable: true,
      },
      {
        name: 'Taxa Serviço',
        selector: 'service_tax',
        sortable: true,
      },
      {
        name: 'Taxa Processamento',
        selector: 'process_tax',
        sortable: true,
      },
      {
        name: 'Banco',
        selector: 'bank',
        sortable: true,
      },
      {
        name: 'Agência',
        selector: 'agency',
        sortable: true,
      },
      {
        name: 'Conta',
        selector: 'account',
        sortable: true,
      },
      {
        name: 'Pix',
        selector: 'pix_key',
        sortable: true,
      },

      {
        name: '',
        selector: 'btns',
        sortable: false,
        cell: (row) => (
          <>
            <button
              type="button"
              onClick={() => handleClickEdit(row)}
              className="btn-icons border-0"
            >
              <KrEdit size={16} color="#fff" />
            </button>
            <button
              type="button"
              onClick={() => handleClickDelete(row)}
              className="btn-icons border-0 ms-3"
            >
              <KrClose size={16} color="#fff" />
            </button>
          </>
        ),
      },
    ],
    [handleClickDelete, handleClickEdit]
  );

  const handleDateContractStart = useCallback((date) => {
    setDateContractStart(date);
  }, []);

  const handleDateContractEnd = useCallback((date) => {
    setDateContractEnd(date);
  }, []);

  const handleChangeContractStatus = useCallback((option) => {
    setContractStatus(option.value);
  }, []);

  const handleChangeServiceTaxType = useCallback((option) => {
    setServiceTaxType(option.id);
  }, []);

  const handleChangeProcessTaxType = useCallback((option) => {
    setProcessTaxType(option.id);
  }, []);

  const handleClose = useCallback(() => {
    if (formRef.current) {
      formRef.current.reset();
    }
    setShow(false);
    setIsEdit(false);
    setIsFree(false);
    setContractSelected({} as IDataItem);
  }, [formRef]);

  const handleSubmit = useCallback(
    async (dataSubmit) => {
      try {
        formRef.current?.setErrors({});
        setSaveContract(true);
        const schema = Yup.object().shape({
          organizer: Yup.string().required('Organizador é obrigatório'),
          fantasy_name: Yup.string().required('O nome fantasia é obrigatório'),
          status: isEdit
            ? Yup.string().required('Status é obrigatório')
            : Yup.string(),
          monthly_tax: Yup.string()
            .required('A Taxa mensal é obrigatória')
            .test(
              'is-positive',
              'A Taxa mensal deve ser maior que zero',
              (value) => {
                if (isCredit) {
                  return value !== '0';
                }
                return true;
              }
            ),
          service_tax: Yup.string().required('Taxa de servico é obrigatória'),
          process_tax: Yup.string().required(
            'Taxa de processamento é obrigatória'
          ),
          free: Yup.string(),
          pix: Yup.string(),
          card: Yup.string(),
          billet: Yup.string(),
          debit: Yup.string(),
          bank: Yup.string(),
          agency: Yup.string(),
          account: Yup.string(),
          pix_key: Yup.string(),
        });

        await schema.validate(dataSubmit, {
          abortEarly: false,
        });

        const formData = {
          organizer_id: dataSubmit.organizer,
          fantasy_name: dataSubmit.fantasy_name,
          start_date: new Date(dataSubmit.date_start),
          end_date: new Date(dataSubmit.date_end),
          tax_type: processTaxType,
          tax:
            processTaxType === 'Porcentagem'
              ? dataSubmit.process_tax
              : parseFloat(
                  dataSubmit.process_tax
                    .replace('R$', '')
                    .replaceAll('.', '')
                    .replace(',', '.')
                ),
          monthly_tax: dataSubmit.monthly_tax,
          service_type: serviceTaxType,
          service:
            serviceTaxType === 'Porcentagem'
              ? dataSubmit.service_tax
              : parseFloat(
                  dataSubmit.service_tax
                    .replace('R$', '')
                    .replaceAll('.', '')
                    .replace(',', '.')
                ),
          koro_deadline: 30,
          organizer_deadline: 40,
          bank: dataSubmit.bank,
          agency: dataSubmit.agency,
          account: dataSubmit.account,
          pix_key: dataSubmit.pix_key,
          status: dataSubmit.status,
        };

        const response = await api.post('/contracts', formData);

        if (dataSubmit.free && isFree) {
          await api.post('contracts-values', {
            contract_id: response.data.id,
            type: 'Gratuito',
            installment: 1,
            tax_type: processTaxType,
            tax:
              processTaxType === 'Porcentagem'
                ? dataSubmit.process_tax
                : parseFloat(
                    dataSubmit.process_tax
                      .replace('R$', '')
                      .replaceAll('.', '')
                      .replace(',', '.')
                  ),
            service_type: serviceTaxType,
            service:
              serviceTaxType === 'Porcentagem'
                ? dataSubmit.service_tax
                : parseFloat(
                    dataSubmit.service_tax
                      .replace('R$', '')
                      .replaceAll('.', '')
                      .replace(',', '.')
                  ),
            koro_deadline: 0,
            organizer_deadline: dataSubmit.free,
          });
        }

        if (dataSubmit.pix && !isFree) {
          await api.post('contracts-values', {
            contract_id: response.data.id,
            type: 'Pix',
            installment: 1,
            tax_type: processTaxType,
            tax:
              processTaxType === 'Porcentagem'
                ? dataSubmit.process_tax
                : parseFloat(
                    dataSubmit.process_tax
                      .replace('R$', '')
                      .replaceAll('.', '')
                      .replace(',', '.')
                  ),
            service_type: serviceTaxType,
            service:
              serviceTaxType === 'Porcentagem'
                ? dataSubmit.service_tax
                : parseFloat(
                    dataSubmit.service_tax
                      .replace('R$', '')
                      .replaceAll('.', '')
                      .replace(',', '.')
                  ),
            koro_deadline: 1,
            organizer_deadline: dataSubmit.pix,
          });
        }

        if (dataSubmit.card && !isFree) {
          await api.post('contracts-values', {
            contract_id: response.data.id,
            type: 'Crédito',
            installment: 1,
            tax_type: processTaxType,
            tax:
              processTaxType === 'Porcentagem'
                ? dataSubmit.process_tax
                : parseFloat(
                    dataSubmit.process_tax
                      .replace('R$', '')
                      .replaceAll('.', '')
                      .replace(',', '.')
                  ),
            service_type: serviceTaxType,
            service:
              serviceTaxType === 'Porcentagem'
                ? dataSubmit.service_tax
                : parseFloat(
                    dataSubmit.service_tax
                      .replace('R$', '')
                      .replaceAll('.', '')
                      .replace(',', '.')
                  ),
            koro_deadline: 1,
            organizer_deadline: dataSubmit.card,
          });
        }

        if (dataSubmit.billet && !isFree) {
          await api.post('contracts-values', {
            contract_id: response.data.id,
            type: 'Boleto',
            installment: 1,
            tax_type: processTaxType,
            tax:
              processTaxType === 'Porcentagem'
                ? dataSubmit.process_tax
                : parseFloat(
                    dataSubmit.process_tax
                      .replace('R$', '')
                      .replaceAll('.', '')
                      .replace(',', '.')
                  ),
            service_type: serviceTaxType,
            service:
              serviceTaxType === 'Porcentagem'
                ? dataSubmit.service_tax
                : parseFloat(
                    dataSubmit.service_tax
                      .replace('R$', '')
                      .replaceAll('.', '')
                      .replace(',', '.')
                  ),
            koro_deadline: 1,
            organizer_deadline: dataSubmit.billet,
          });
        }

        if (dataSubmit.debit && !isFree) {
          await api.post('contracts-values', {
            contract_id: response.data.id,
            type: 'Débito',
            installment: 1,
            tax_type: processTaxType,
            tax:
              processTaxType === 'Porcentagem'
                ? dataSubmit.process_tax
                : parseFloat(
                    dataSubmit.process_tax
                      .replace('R$', '')
                      .replaceAll('.', '')
                      .replace(',', '.')
                  ),
            service_type: serviceTaxType,
            service:
              serviceTaxType === 'Porcentagem'
                ? dataSubmit.service_tax
                : parseFloat(
                    dataSubmit.service_tax
                      .replace('R$', '')
                      .replaceAll('.', '')
                      .replace(',', '.')
                  ),
            koro_deadline: 1,
            organizer_deadline: dataSubmit.debit,
          });
        }

        handleLoadContracts(1);

        setSaveContract(false);
        Toast.fire('Sucesso!', 'Contrato criado com sucesso', 'success');
        handleClose();
      } catch (error) {
        setSaveContract(false);
        if (error instanceof Yup.ValidationError) {
          const errors = getValidationErros(error);
          formRef.current?.setErrors(errors);
        } else {
          Swal.fire('Oops...', 'Ocorreu um erro tente novamente, por favor');
        }
      }
    },
    [
      handleClose,
      handleLoadContracts,
      isCredit,
      isEdit,
      isFree,
      processTaxType,
      serviceTaxType,
    ]
  );

  const handleUpdateSubmit = useCallback(
    async (dataSubmit) => {
      try {
        formRef.current?.setErrors({});
        setSaveContract(true);
        const schema = Yup.object().shape({
          organizer: Yup.string().required('Organizador é obrigatório'),
          fantasy_name: Yup.string().required('O nome fantasia é obrigatório'),
          status: isEdit
            ? Yup.string().required('Status é obrigatório')
            : Yup.string(),
          service_tax: Yup.string().required('Taxa de servico é obrigatória'),
          process_tax: Yup.string().required(
            'Taxa de processamento é obrigatória'
          ),
          monthly_tax: Yup.string().required('A Taxa mensal é obrigatória'),
          free: Yup.string(),
          pix: Yup.string(),
          card: Yup.string(),
          billet: Yup.string(),
          debit: Yup.string(),
          bank: Yup.string(),
          agency: Yup.string(),
          account: Yup.string(),
          pix_key: Yup.string(),
        });

        await schema.validate(dataSubmit, {
          abortEarly: false,
        });

        const constractFormData = {
          organizer_id: dataSubmit.organizer,
          fantasy_name: dataSubmit.fantasy_name,
          start_date: new Date(dataSubmit.date_start),
          end_date: new Date(dataSubmit.date_end),
          tax_type: processTaxType,
          tax:
            processTaxType === 'Porcentagem'
              ? dataSubmit.process_tax
              : parseFloat(
                  dataSubmit.process_tax
                    .replace('R$', '')
                    .replaceAll('.', '')
                    .replace(',', '.')
                ),
          service_type: serviceTaxType,
          service:
            serviceTaxType === 'Porcentagem'
              ? dataSubmit.service_tax
              : parseFloat(
                  dataSubmit.service_tax
                    .replace('R$', '')
                    .replaceAll('.', '')
                    .replace(',', '.')
                ),
          monthly_tax: dataSubmit.monthly_tax,
          koro_deadline: 30,
          organizer_deadline: 40,
          bank: dataSubmit.bank,
          agency: dataSubmit.agency,
          account: dataSubmit.account,
          pix_key: dataSubmit.pix_key,
          status: dataSubmit.status,
        };

        const response = await api.put(
          `/contracts/${contractSelected.id}`,
          constractFormData
        );

        if (dataSubmit.free) {
          const formData = {
            contract_id: response.data.id,
            type: 'Gratuito',
            installment: 1,
            tax_type: processTaxType,
            tax:
              processTaxType === 'Porcentagem'
                ? dataSubmit.process_tax
                : parseFloat(
                    dataSubmit.process_tax
                      .replace('R$', '')
                      .replaceAll('.', '')
                      .replace(',', '.')
                  ),
            service_type: serviceTaxType,
            service:
              serviceTaxType === 'Porcentagem'
                ? dataSubmit.service_tax
                : parseFloat(
                    dataSubmit.service_tax
                      .replace('R$', '')
                      .replaceAll('.', '')
                      .replace(',', '.')
                  ),
            koro_deadline: 0,
            organizer_deadline: dataSubmit.free,
          };
          if (contractSelected.free_id) {
            await api.put(
              `contracts-values/${contractSelected.free_id}`,
              formData
            );
          } else {
            await api.post('contracts-values', formData);
          }
        } else if (contractSelected.free_id) {
          await api.delete(`contracts-values/${contractSelected.free_id}`);
        }

        if (dataSubmit.pix) {
          if (!isFree) {
            const formData = {
              contract_id: response.data.id,
              type: 'Pix',
              installment: 1,
              tax_type: processTaxType,
              tax:
                processTaxType === 'Porcentagem'
                  ? dataSubmit.process_tax
                  : parseFloat(
                      dataSubmit.process_tax
                        .replace('R$', '')
                        .replaceAll('.', '')
                        .replace(',', '.')
                    ),
              service_type: serviceTaxType,
              service:
                serviceTaxType === 'Porcentagem'
                  ? dataSubmit.service_tax
                  : parseFloat(
                      dataSubmit.service_tax
                        .replace('R$', '')
                        .replaceAll('.', '')
                        .replace(',', '.')
                    ),
              koro_deadline: 1,
              organizer_deadline: dataSubmit.pix,
            };
            if (contractSelected.pix_id) {
              await api.put(
                `contracts-values/${contractSelected.pix_id}`,
                formData
              );
            } else {
              await api.post('contracts-values', formData);
            }
          } else if (contractSelected.pix_id) {
            await api.delete(`contracts-values/${contractSelected.pix_id}`);
          }
        } else if (contractSelected.pix_id) {
          await api.delete(`contracts-values/${contractSelected.pix_id}`);
        }

        if (dataSubmit.card) {
          if (!isFree) {
            const formData = {
              contract_id: response.data.id,
              type: 'Crédito',
              installment: 1,
              tax_type: processTaxType,
              tax:
                processTaxType === 'Porcentagem'
                  ? dataSubmit.process_tax
                  : parseFloat(
                      dataSubmit.process_tax
                        .replace('R$', '')
                        .replaceAll('.', '')
                        .replace(',', '.')
                    ),
              service_type: serviceTaxType,
              service:
                serviceTaxType === 'Porcentagem'
                  ? dataSubmit.service_tax
                  : parseFloat(
                      dataSubmit.service_tax
                        .replace('R$', '')
                        .replaceAll('.', '')
                        .replace(',', '.')
                    ),
              koro_deadline: 1,
              organizer_deadline: dataSubmit.card,
            };
            if (contractSelected.card_id) {
              await api.put(
                `contracts-values/${contractSelected.card_id}`,
                formData
              );
            } else {
              await api.post('contracts-values', formData);
            }
          } else if (contractSelected.card_id) {
            await api.delete(`contracts-values/${contractSelected.card_id}`);
          }
        } else if (contractSelected.card_id) {
          await api.delete(`contracts-values/${contractSelected.card_id}`);
        }

        if (dataSubmit.billet) {
          if (!isFree) {
            const formData = {
              contract_id: response.data.id,
              type: 'Boleto',
              installment: 1,
              tax_type: processTaxType,
              tax:
                processTaxType === 'Porcentagem'
                  ? dataSubmit.process_tax
                  : parseFloat(
                      dataSubmit.process_tax
                        .replace('R$', '')
                        .replaceAll('.', '')
                        .replace(',', '.')
                    ),
              service_type: serviceTaxType,
              service:
                serviceTaxType === 'Porcentagem'
                  ? dataSubmit.service_tax
                  : parseFloat(
                      dataSubmit.service_tax
                        .replace('R$', '')
                        .replaceAll('.', '')
                        .replace(',', '.')
                    ),
              koro_deadline: 1,
              organizer_deadline: dataSubmit.billet,
            };
            if (contractSelected.billet_id) {
              await api.put(
                `contracts-values/${contractSelected.billet_id}`,
                formData
              );
            } else {
              await api.post('contracts-values', formData);
            }
          } else if (contractSelected.billet_id) {
            await api.delete(`contracts-values/${contractSelected.billet_id}`);
          }
        } else if (contractSelected.billet_id) {
          await api.delete(`contracts-values/${contractSelected.billet_id}`);
        }

        if (dataSubmit.debit) {
          if (!isFree) {
            const formData = {
              contract_id: response.data.id,
              type: 'Débito',
              installment: 1,
              tax_type: processTaxType,
              tax:
                processTaxType === 'Porcentagem'
                  ? dataSubmit.process_tax
                  : parseFloat(
                      dataSubmit.process_tax
                        .replace('R$', '')
                        .replaceAll('.', '')
                        .replace(',', '.')
                    ),
              service_type: serviceTaxType,
              service:
                serviceTaxType === 'Porcentagem'
                  ? dataSubmit.service_tax
                  : parseFloat(
                      dataSubmit.service_tax
                        .replace('R$', '')
                        .replaceAll('.', '')
                        .replace(',', '.')
                    ),
              koro_deadline: 1,
              organizer_deadline: dataSubmit.debit,
            };

            if (contractSelected.debit_id) {
              await api.put(
                `contracts-values/${contractSelected.debit_id}`,
                formData
              );
            } else {
              await api.post('contracts-values', formData);
            }
          } else if (contractSelected.debit_id) {
            await api.delete(`contracts-values/${contractSelected.debit_id}`);
          }
        } else if (contractSelected.debit_id) {
          await api.delete(`contracts-values/${contractSelected.debit_id}`);
        }

        handleLoadContracts(pageSelected);
        setSaveContract(false);
        Toast.fire('Sucesso!', 'Contrato editado com sucesso', 'success');

        handleClose();
      } catch (error) {
        setSaveContract(false);
        if (error instanceof Yup.ValidationError) {
          const errors = getValidationErros(error);
          formRef.current?.setErrors(errors);
        } else {
          Swal.fire('Oops...', 'Ocorreu um erro tente novamente, por favor');
        }
      }
    },
    [
      isEdit,
      processTaxType,
      serviceTaxType,
      contractSelected.id,
      contractSelected.free_id,
      contractSelected.pix_id,
      contractSelected.card_id,
      contractSelected.billet_id,
      contractSelected.debit_id,
      handleLoadContracts,
      pageSelected,
      handleClose,
      isFree,
    ]
  );

  const handleChangePage = useCallback(
    (page) => {
      if (page <= tableData.totalPage) {
        handleLoadContracts(
          page,
          organizerSelected,
          fantasyNameSelected,
          dateStartSelected,
          dateEndSelected
        );
        setPageSelected(page);
      }
    },
    [
      dateEndSelected,
      dateStartSelected,
      fantasyNameSelected,
      handleLoadContracts,
      organizerSelected,
      tableData.totalPage,
    ]
  );

  const handleSubmitFilters = useCallback(
    (formData) => {
      setOrganizerSelected(
        formData.organizer !== 'todos' ? formData.organizer : ''
      );
      setFantasyNameSelected(formData.name);
      setDateStartSelected(formData.dateStart);
      setDateEndSelected(formData.dateEnd);
      handleLoadContracts(
        1,
        formData.organizer !== 'todos' ? formData.organizer : undefined,
        formData.name,
        formData.dateStart,
        formData.dateEnd
      );
      setPageSelected(1);
    },
    [handleLoadContracts]
  );

  const handleFree = useCallback((e) => {
    if (e.target.value.length > 0) {
      setIsFree(true);
    } else {
      setIsFree(false);
    }
  }, []);

  const handleIsCredit = useCallback((e) => {
    if (e.target.value.length > 0) {
      setIsCredit(true);
    } else {
      setIsCredit(false);
    }
  }, []);

  return (
    <Container>
      <div className="container-fluid">
        <div className="row">
          <div className="zoom col-12 px-0">
            <div className="bg-white d-sm-flex justify-content-between align-items-center p-4">
              <h1 className="mb-0">Contratos</h1>

              {/* <ExportCsv
                data={data}
                columns={columns}
                fileName="Lista_de_eventos"
              /> */}
              <button
                type="button"
                onClick={() => setShow(true)}
                className="mb-0 h6 fw-normal text-white btn-new px-4 py-2"
              >
                <HiPlus size={16} color="#fff" className="me-2" />
                Novo Contrato
              </button>
            </div>
          </div>
          <Filter className="col-12 px-0 mt-3">
            <FiltersLists
              onSubmitFilters={handleSubmitFilters}
              organizer
              organizersData={organizers}
              name
              date
            />
          </Filter>
          <div className="col-12 px-0">
            <div className="row">
              <TableList className="col-12 mt-3">
                <div className="bg-white pb-2">
                  <div className="bg-white overflow-auto">
                    <div className="">
                      <Table
                        className="mb-0 p-0 table"
                        columns={columns}
                        data={data}
                        pagination
                        totalData={tableData.total}
                        selectedPage={pageSelected}
                        onChangePage={handleChangePage}
                      />
                    </div>
                  </div>
                </div>
              </TableList>
            </div>
          </div>
        </div>
      </div>
      <Modal
        className="zoom modal-filter"
        size="lg"
        centered
        show={show}
        onHide={handleClose}
      >
        <Form
          ref={formRef}
          initialData={contractSelected}
          onSubmit={!isEdit ? handleSubmit : handleUpdateSubmit}
        >
          <Modal.Header className="justify-content-between align-items-center border-0 px-3 px-sm-5 pb-0 pt-0">
            <Modal.Title className="mb-0 mt-4">
              <h2 className="title mb-0">
                {!isEdit ? 'Novo contrato' : 'Editar contrato'}
              </h2>
            </Modal.Title>
            <button
              type="button"
              onClick={handleClose}
              className="border-0 bg-transparent"
            >
              <KrClose size={20} color="#989898" />
            </button>
          </Modal.Header>
          <Modal.Body className="container pt-0 px-3 px-sm-5">
            <div className="row">
              <div className="col-12">
                <hr className="mb-4" />
              </div>
              <div className={`col-lg-${isEdit ? '4' : '6'}`}>
                <h4 className="h6 fw-normal">Organizador</h4>
                <Select
                  name="organizer"
                  className="height"
                  placeholder="Selecione"
                  options={organizers}
                />
              </div>
              <div className={`col-lg-${isEdit ? '4' : '6'} mt-3 mt-lg-0`}>
                <h4 className="h6 fw-normal">Nome fantasia</h4>
                <Input name="fantasy_name" type="text" className="height" />
              </div>
              {isEdit && (
                <div className="col-lg-4 mt-3 mt-lg-0">
                  <h4 className="h6 fw-normal">Status</h4>
                  <Select
                    name="status"
                    className="height"
                    placeholder="Selecione"
                    options={[
                      {
                        id: 'Ativo',
                        value: 'Ativo',
                        selected: contractStatus === 'Ativo',
                      },
                      {
                        id: 'Encerrado',
                        value: 'Encerrado',
                        selected: contractStatus === 'Encerrado',
                      },
                      {
                        id: 'Cancelado',
                        value: 'Cancelado',
                        selected: contractStatus === 'Cancelado',
                      },
                    ]}
                    onChange={handleChangeContractStatus}
                  />
                </div>
              )}
              <div className="col-lg-4 mt-3">
                <h4 className="h6 fw-normal">Data início</h4>
                <InputDate
                  className="height-date"
                  name="date_start"
                  dateFormat="dd/MM/yyyy"
                  selected={dateContractStart}
                  onChange={handleDateContractStart}
                  colorSvg="#5D5D5D"
                />
              </div>
              <div className="col-lg-4 mt-3">
                <h4 className="h6 fw-normal">Data fim</h4>
                <InputDate
                  className="height-date"
                  name="date_end"
                  dateFormat="dd/MM/yyyy"
                  selected={dateContractEnd}
                  onChange={handleDateContractEnd}
                  colorSvg="#5D5D5D"
                />
              </div>
              <div className="col-lg-4 mt-3">
                <h4 className="h6 fw-normal">Taxa mensal (%)</h4>
                <Input
                  name="monthly_tax"
                  type="number"
                  step="0.01"
                  className="height mt-0"
                  value={isFree ? 0 : undefined}
                  defaultValue={
                    Object.keys(contractSelected).length > 0
                      ? parseFloat(
                          contractSelected.monthly_tax.replace('%', '')
                        )
                      : undefined
                  }
                  max={100}
                  min={0}
                />
              </div>
              <div className="col-lg-6 mt-3">
                <h4 className="h6 fw-normal">Taxa de serviço (% ou Valor)</h4>
                <div className="row">
                  <div className="col-6">
                    <Select
                      name="type_service_tax"
                      className="height me-2"
                      placeholder="Selecione"
                      options={[
                        {
                          id: 'Porcentagem',
                          value: 'Percentual',
                          selected: serviceTaxType === 'Porcentagem',
                        },
                        {
                          id: 'Preço',
                          value: 'Valor',
                          selected: serviceTaxType === 'Preço',
                        },
                      ]}
                      onChange={handleChangeServiceTaxType}
                    />
                  </div>
                  <div className="col-6">
                    {serviceTaxType === 'Porcentagem' ? (
                      <Input
                        name="service_tax"
                        type="number"
                        step="0.01"
                        className="height mt-0"
                        value={isFree ? 0 : undefined}
                        defaultValue={
                          Object.keys(contractSelected).length > 0
                            ? parseFloat(
                                contractSelected.service_tax.replace('%', '')
                              )
                            : undefined
                        }
                      />
                    ) : (
                      <InputMask
                        kind="money"
                        placeholder="R$ 0,00"
                        name="service_tax"
                        className="input mt-0 height"
                        value={isFree ? '0' : contractSelected.service_tax}
                      />
                    )}
                  </div>
                </div>
              </div>
              <div className="col-lg-6 mt-3">
                <h4 className="h6 fw-normal">
                  Taxa de processamento (% ou Valor)
                </h4>
                <div className="row">
                  <div className="col-6">
                    <Select
                      name="type_process_tax"
                      className="height me-2"
                      placeholder="Selecione"
                      options={[
                        {
                          id: 'Porcentagem',
                          value: 'Percentual',
                          selected: processTaxType === 'Porcentagem',
                        },
                        {
                          id: 'Preço',
                          value: 'Valor',
                          selected: processTaxType === 'Preço',
                        },
                      ]}
                      onChange={handleChangeProcessTaxType}
                    />
                  </div>
                  <div className="col-6">
                    {processTaxType === 'Porcentagem' ? (
                      <Input
                        name="process_tax"
                        type="number"
                        step="0.01"
                        className="height mt-0"
                        value={isFree ? 0 : undefined}
                        defaultValue={
                          Object.keys(contractSelected).length > 0
                            ? parseFloat(
                                contractSelected.process_tax.replace('%', '')
                              )
                            : undefined
                        }
                      />
                    ) : (
                      <InputMask
                        kind="money"
                        placeholder="R$ 0,00"
                        name="process_tax"
                        className="input mt-0 height"
                        value={isFree ? '0' : contractSelected.process_tax}
                      />
                    )}
                  </div>
                </div>
              </div>
              <h4 className="h5 mt-4">
                Prazos para a liberação das vendas em dias corridos para saque
              </h4>
              <div className="col-12">
                <div className="row">
                  <div className="col-lg">
                    <span>Gratuito</span>
                    <Input
                      name="free"
                      type="number"
                      className="height mt-0"
                      onChange={handleFree}
                    />
                  </div>
                  <div
                    className={`${isFree ? 'opacity-50 pe-none' : ''} col-lg`}
                  >
                    <span>PIX</span>
                    <Input name="pix" type="number" className="height mt-0" />
                  </div>
                  <div
                    className={`${isFree ? 'opacity-50 pe-none' : ''} col-lg`}
                  >
                    <span>Boleto</span>
                    <Input
                      name="billet"
                      type="number"
                      className="height mt-0"
                    />
                  </div>
                  <div
                    className={`${isFree ? 'opacity-50 pe-none' : ''} col-lg`}
                  >
                    <span>Débito</span>
                    <Input name="debit" type="number" className="height mt-0" />
                  </div>
                  <div
                    className={`${isFree ? 'opacity-50 pe-none' : ''} col-lg`}
                  >
                    <span>Crédito</span>
                    <Input
                      name="card"
                      type="number"
                      onChange={handleIsCredit}
                      className="height mt-0"
                    />
                  </div>
                </div>
              </div>
              <h4 className="h5 mt-4">Dados bancários</h4>
              <div className="col-lg-4">
                <span>Banco</span>
                <Input name="bank" type="text" className="height mt-0" />
              </div>
              <div className="col-lg-4">
                <span>Agência</span>
                <Input name="agency" type="text" className="height mt-0" />
              </div>
              <div className="col-lg-4">
                <span>Conta</span>
                <Input name="account" type="text" className="height mt-0" />
              </div>
              <div className="col-12 mt-3">
                <span>Chave PIX</span>
                <Input name="pix_key" type="text" className="height mt-0" />
              </div>
            </div>
          </Modal.Body>
          <Modal.Footer className="border-0 py-4 px-5">
            <button
              type="submit"
              className="ms-auto btn-filter d-flex align-items-center justify-content-center btn h6 fw-normal py-2 px-4 mt-2 mb-0"
            >
              Salvar
            </button>
          </Modal.Footer>
        </Form>
      </Modal>
      <Loading
        active={loading || saveContract}
        text={saveContract ? 'Salvando...' : 'Carregando...'}
      />
    </Container>
  );
};

export default Contracts;
